/**
 * Bottom sheet that opens from GroupBanner. Shows the participant roster +
 * the two host actions: share invite link, leave group.
 *
 * Sharing prefers the native share sheet (mobile) and falls back to
 * navigator.clipboard so guests can paste into WhatsApp/iMessage manually.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { BottomSheet } from "./BottomSheet";
import { Avatar } from "./Avatar";
import { useGroupCart } from "@/store/group-cart";
import { resolveSlug } from "@/lib/resolveSlug";

export interface GroupSheetProps {
  open: boolean;
  onClose: () => void;
}

export function buildInviteUrl(opts: {
  branchId: string;
  tableId: string;
  inviteCode: string;
}): string {
  const origin = window.location.origin;
  // Use the resolved storefront basename, not window.location.pathname.
  // The basename keeps `/:slug` for path-routed tenants (without it the
  // recipient's BranchProvider resolves no tenant, so no `X-API-KEY` is sent
  // and the join 401s), while pinning the link to the storefront root —
  // HomeScreen is the only screen that forwards `?inviteCode=` on to
  // /group/join, so sharing from /cart or /menu must not carry that path over.
  const { basename } = resolveSlug();
  const usp = new URLSearchParams({
    branchId: opts.branchId,
    tableId: opts.tableId,
    inviteCode: opts.inviteCode,
  });
  return `${origin}${basename}/?${usp.toString()}`;
}

export function GroupSheet({ open, onClose }: GroupSheetProps) {
  const { t } = useTranslation();
  const { cart, isHost, leave } = useGroupCart();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [leaving, setLeaving] = useState(false);

  if (!cart) return null;

  const url = buildInviteUrl({
    branchId: cart.branchId,
    tableId: cart.tableId,
    inviteCode: cart.inviteCode,
  });

  const handleShare = async () => {
    try {
      if (typeof navigator !== "undefined" && "share" in navigator) {
        await (navigator as Navigator & { share: (d: ShareData) => Promise<void> }).share({
          url,
          title: t("v2.group_cart.share_invite"),
          text: t("v2.group_cart.offer_title"),
        });
        return;
      }
    } catch {
      // User cancelled / share unsupported — fall through to clipboard.
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      /* nothing else we can do; UI shows nothing-happened. */
    }
  };

  const handleLeave = async () => {
    setLeaving(true);
    try {
      await leave();
      onClose();
      navigate("/");
    } catch {
      setLeaving(false);
    }
  };

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      ariaLabel={t("v2.group_cart.view_group")}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div>
          <div className="display" style={{ fontSize: 18, fontWeight: 800, color: "var(--ink-1)" }}>
            {t("v2.group_cart.active_label")}
          </div>
          <div style={{ fontSize: 13, color: "var(--ink-3)", marginTop: 2 }}>
            {t("v2.group_cart.people_count", {
              count: cart.participants.length,
              defaultValue: `${cart.participants.length} people`,
            })}
          </div>
        </div>

        {/* Invite code — prominent so the host can read it out loud while
            friends type it in. */}
        <div
          style={{
            background: "var(--primary-50)",
            border: "1px solid var(--primary-100)",
            borderRadius: "var(--radius-card)",
            padding: "12px 16px",
            display: "flex",
            alignItems: "center",
            gap: 14,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "var(--primary-700)",
                letterSpacing: 0.6,
                textTransform: "uppercase",
              }}
            >
              {t("v2.group_cart.invite_code_label", {
                defaultValue: "Invite code",
              })}
            </div>
            <div className="display"
              style={{
                fontFamily:
                  "ui-monospace, SFMono-Regular, Menlo, monospace",
                fontSize: 28,
                fontWeight: 800,
                letterSpacing: 8,
                color: "var(--primary-800, #1e3a8a)",
                marginTop: 2,
              }}
              data-testid="group-code"
              data-group-id={cart.groupCartId}
            >
              {cart.inviteCode}
            </div>
          </div>
          <button
            data-testid="group-copy-link"
            onClick={async () => {
              // Copy the full join URL, not just the 4-char code — guests
              // who get the bare code rarely know where to type it in. The
              // URL contains branchId, tableId, and inviteCode so it lands
              // them directly on the join screen.
              try {
                await navigator.clipboard.writeText(url);
                setCopied(true);
                setTimeout(() => setCopied(false), 1800);
              } catch {
                /* nothing else we can do */
              }
            }}
            aria-label={t("v2.group_cart.copy_link", {
              defaultValue: "Copy link",
            })}
            style={{
              height: 38,
              padding: "0 14px",
              borderRadius: 10,
              background: "var(--surface-1)",
              border: "1px solid var(--primary-200, #bfdbfe)",
              color: "var(--primary-700)",
              fontSize: 13,
              fontWeight: 700,
              cursor: "pointer",
              fontFamily: "inherit",
              whiteSpace: "nowrap",
            }}
          >
            {copied
              ? t("v2.group_cart.invite_link_copied", { defaultValue: "Copied" })
              : t("v2.group_cart.copy_link", { defaultValue: "Copy link" })}
          </button>
        </div>

        <div
          style={{
            background: "var(--surface-2)",
            borderRadius: "var(--radius-card)",
            padding: "4px 12px",
          }}
        >
          {cart.participants.map((p, i) => (
            <div
              key={p.guestId}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "12px 4px",
                borderBottom:
                  i === cart.participants.length - 1
                    ? 0
                    : "1px solid var(--border)",
              }}
            >
              <Avatar participant={p} size={36} showVerified />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span className="display"
                    style={{
                      fontSize: 14,
                      fontWeight: 700,
                      color: "var(--ink-1)",
                    }}
                  >
                    {p.displayName}
                  </span>
                  {p.guestId === cart.createdByGuestId && (
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        padding: "2px 6px",
                        borderRadius: 4,
                        background: "var(--ink-1)",
                        color: "#fff",
                        letterSpacing: 0.3,
                      }}
                    >
                      {t("v2.group_cart.host_badge")}
                    </span>
                  )}
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: "var(--ink-3)",
                    marginTop: 2,
                  }}
                >
                  {p.verified
                    ? t("v2.group_cart.verified")
                    : t("v2.group_cart.unverified")}
                </div>
              </div>
            </div>
          ))}
        </div>

        <button
          onClick={handleShare}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            width: "100%",
            height: 48,
            borderRadius: 12,
            background: "var(--primary-600)",
            color: "#fff",
            border: "none",
            fontSize: 15,
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          {copied
            ? t("v2.group_cart.invite_link_copied")
            : t("v2.group_cart.share_invite")}
        </button>

        {!isHost && (
          <button
            data-testid="group-leave"
            onClick={handleLeave}
            disabled={leaving}
            style={{
              width: "100%",
              height: 44,
              borderRadius: 12,
              background: "transparent",
              color: "#b91c1c",
              border: "1px solid #fecaca",
              fontSize: 14,
              fontWeight: 700,
              cursor: leaving ? "wait" : "pointer",
            }}
          >
            {leaving ? "…" : t("v2.group_cart.leave_group")}
          </button>
        )}
      </div>
    </BottomSheet>
  );
}
